import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Clock, Users, Mail, Ban } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { adminApi } from "@/lib/api";
import { PendingRequests } from "@/components/team/PendingRequests";
import { MembersList } from "@/components/team/MembersList";
import { Invitations } from "@/components/team/Invitations";
import { RolesMatrix } from "@/components/team/RolesMatrix";
import { AccessHistory } from "@/components/team/AccessHistory";

function Stat({ icon: Icon, tone, value, label, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
      <Icon className={`h-5 w-5 ${tone}`} />
      <p className="mt-3 font-serif text-2xl font-semibold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const load = useCallback(async () => {
    try {
      const [u, i, l] = await Promise.all([adminApi.listUsers(), adminApi.listInvites(), adminApi.logs()]);
      setUsers(u); setInvites(i); setLogs(l);
    } catch {
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (u, action, role) => {
    if (action === "suspend" && !window.confirm(`Suspend ${u.email}? They will be signed out immediately.`)) return;
    if (action === "reject" && !window.confirm(`Reject access request from ${u.email}?`)) return;
    try {
      if (action === "approve") await adminApi.approve(u.user_id, role);
      if (action === "reject") await adminApi.reject(u.user_id);
      if (action === "suspend") await adminApi.suspend(u.user_id);
      if (action === "role") await adminApi.setRole(u.user_id, role);
      toast.success("Access updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const pending = users.filter((u) => u.status === "pending");
  const count = (s) => users.filter((u) => u.status === s).length;
  const pendingInvites = invites.filter((i) => i.status === "pending").length;

  const tabs = [
    { id: "pending", label: "Pending Requests", count: pending.length },
    { id: "members", label: "Team Members", count: count("approved") },
    { id: "invite", label: "Invitations", count: pendingInvites },
    { id: "roles", label: "Roles & Permissions" },
    { id: "history", label: "Access History" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <p className="eyebrow text-gold">Administration</p>
        <h1 className="mt-2 font-serif text-3xl font-semibold tracking-tight sm:text-4xl">Team management</h1>
        <p className="mt-2 text-muted-foreground">Approve access requests, invite teammates, assign roles and review every access change.</p>

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat icon={Clock} tone="text-amber-600" value={pending.length} label="Pending requests" testId="team-stat-pending" />
          <Stat icon={Users} tone="text-emerald-600" value={count("approved")} label="Active members" testId="team-stat-active" />
          <Stat icon={Mail} tone="text-gold" value={pendingInvites} label="Open invitations" testId="team-stat-invites" />
          <Stat icon={Ban} tone="text-zinc-500" value={count("suspended") + count("rejected")} label="Suspended / rejected" testId="team-stat-blocked" />
        </div>

        <div className="mt-8 flex gap-2 overflow-x-auto pb-1">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`team-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.id ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>
              {t.label}
              {t.count !== undefined && <span className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-bold ${tab === t.id ? "bg-gold text-primary" : "bg-secondary"}`}>{t.count}</span>}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {loading ? (
            [0, 1, 2].map((i) => <div key={i} className="mb-3 h-20 animate-pulse rounded-2xl bg-secondary/60" />)
          ) : (
            <>
              {tab === "pending" && <PendingRequests users={pending} onAct={act} />}
              {tab === "members" && <MembersList users={users} onAct={act} />}
              {tab === "invite" && <Invitations invites={invites} onChange={load} />}
              {tab === "roles" && <RolesMatrix />}
              {tab === "history" && <AccessHistory logs={logs} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
